import * as React from 'react';
import EmailLink from 'components/EmailLink';
import { COMPANY_EMAIL } from 'constants/internal';
import { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { MdInfoOutline, MdLockOpen } from 'react-icons/md';
import { IoRocketOutline } from 'react-icons/io5';
import { FaGraduationCap } from 'react-icons/fa6';
import { PlayIcon } from '@heroicons/react/solid';

import Link from 'components/Link';
import YouTubeLightbox from 'screens/YouTubeLightbox';
import { useModal } from 'hooks/useModal';

const VIDEO_ID = 'Uj-T2Ibo7Xg';

interface WelcomeSlidePanelProps {
  className?: string;
}

const WelcomeSlidePanel: React.FC<WelcomeSlidePanelProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isOpen: isVideoOpen,
    openModal: openVideoModal,
    closeModal: closeVideoModal,
  } = useModal();

  return (
    <>
      <button
        className={`ml-2 text-graph-purple transition-colors hover:text-purple-700 ${className}`}
        onClick={() => setIsOpen(true)}
        title="Help & Getting Started"
      >
        <MdInfoOutline className="text-graph-purple" />
      </button>

      <Transition.Root show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-40" onClose={setIsOpen}>
          <Transition.Child
            as={React.Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={React.Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-sm flex-col border-r border-brand-primary bg-palette-black-3 shadow-xl">
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute right-4 top-4 z-10 text-white transition-colors hover:text-gray-300"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                <div className="flex h-full flex-col">
                  <div className="flex-1 overflow-y-auto p-6">
                    <h1 className="mb-6 justify-center text-xl font-semibold text-white">
                      Welcome to InsiderFinance!
                      <div className="border-gradient mt-2 h-0.5 w-16 bg-gradient-to-r from-blue-500 to-teal-500"></div>
                    </h1>

                    <div className="mb-8 space-y-6">
                      <p className="text-sm font-semibold text-white">
                        <IoRocketOutline className="inline-block h-5 w-5" /> Ready to spot
                        high-potential trades with institutional-level data? Visit the links below
                        to hit the ground running:
                      </p>
                      <div>
                        <h2 className="mb-3 flex items-center text-lg font-bold">
                          <FaGraduationCap className="mr-2" />{' '}
                          <span className="text-brand-green-primary">Free Resources</span>
                        </h2>
                        <div className="space-y-2 pl-4 text-sm">
                          <ul className="list-inside list-disc space-y-2">
                            <li>
                              <Link
                                isExternal
                                href="https://www.insiderfinance.io/learn"
                                className="font-medium italic underline"
                              >
                                Enroll in our Masterclass
                              </Link>{' '}
                              — quick lessons, instant results
                            </li>
                            <li>
                              <Link
                                isExternal
                                href="https://discord.gg/s6htT3aK6W"
                                className="font-medium italic underline"
                              >
                                Join our Discord
                              </Link>{' '}
                              — trending news + AI insights
                            </li>
                            <li>
                              <Link
                                isExternal
                                href="https://www.insiderfinance.io/resources"
                                className="font-medium italic underline"
                              >
                                Visit the Resource Center
                              </Link>{' '}
                              — deep dives, step-by-steps
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div>
                        <h2 className="mb-3 flex items-center text-lg font-bold">
                          <MdLockOpen className="mr-2" />
                          <span className="text-brand-green-primary">After Joining</span>
                        </h2>
                        <div className="space-y-2 pl-4 text-sm">
                          <ul className="list-inside list-disc space-y-2">
                            <li>
                              <Link
                                isExternal
                                href="https://www.insiderfinance.io/my/tradingview"
                                className="italic underline"
                              >
                                Unlock Automated Technical Analysis
                              </Link>{' '}
                              in minutes
                            </li>
                            <li>
                              <Link
                                isExternal
                                href="https://www.insiderfinance.io/api/auth/discord"
                                className="font-medium italic underline"
                              >
                                Access Member-Only Discord Channels
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-sm text-white">
                        If you ever need help, you can always contact us at
                        <span className="px-1 font-bold italic underline">
                          <EmailLink email={COMPANY_EMAIL} placeholder="our support address" />
                        </span>
                      </p>
                      <p className="text-sm text-white">
                        Let's get started! Your edge in the market begins now.
                      </p>

                      {/* YouTube Video Thumbnail */}
                      <div className="mt-6">
                        <button
                          onClick={() => openVideoModal()}
                          className="group relative w-full overflow-hidden rounded-lg shadow-lg transition-all hover:scale-105"
                        >
                          <img
                            src={`https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`}
                            alt="InsiderFinance Introduction Video"
                            className="h-auto w-full"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 transition-all group-hover:bg-opacity-40">
                            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-red-500 shadow-lg transition-all group-hover:scale-110">
                              <PlayIcon className="h-10 w-10 text-white" />
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-center space-x-3">
                      <button
                        onClick={() => setIsOpen(false)}
                        className="rounded bg-blue-600 px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      <YouTubeLightbox
        title="InsiderFinance: Level the Playing Field"
        videoId={VIDEO_ID}
        isOpen={isVideoOpen}
        handleClose={closeVideoModal}
      />
    </>
  );
};

export default WelcomeSlidePanel;
